/**
 * Recording Protection Hook
 * Unified hook that combines all protection features for recording
 * - App lifecycle monitoring (foreground/background)
 * - Network connectivity tracking
 * - Chunk persistence status
 * - Interruption handling
 */

import { useState, useEffect, useCallback } from 'react';
import { appLifecycleService, AppLifecycleState } from '@/services/appLifecycle.service';
import { networkMonitorService, NetworkState } from '@/services/networkMonitor.service';
import { useRecordingSessionStore, RecordingSession } from '@/stores/recordingSessionStore';
import { chunkStorageService } from '@/services/chunkStorage.service';

export interface RecordingProtectionState {
  // Network status
  isOnline: boolean;
  networkState: NetworkState;

  // App lifecycle
  isAppActive: boolean;
  appState: AppLifecycleState;

  // Interruption tracking
  wasInterrupted: boolean;
  interruptionReason?: RecordingSession['interruptionReason'];

  // Chunk persistence
  savedChunksCount: number;
  lastChunkSavedAt: Date | null;
  isChunkStorageActive: boolean;

  // Recovery
  hasInterruptedSessions: boolean;
  interruptedSessionCount: number;
}

export interface UseRecordingProtectionOptions {
  onAppBackground?: () => void;
  onAppForeground?: () => void;
  onNetworkOffline?: () => void;
  onNetworkOnline?: () => void;
  onInterruption?: (reason: RecordingSession['interruptionReason']) => void;
}

export interface UseRecordingProtectionReturn extends RecordingProtectionState {
  // Methods
  initialize: () => Promise<void>;
  getInterruptedSessions: () => RecordingSession[];
  clearInterruptedSession: (sessionId: string) => void;
  clearAllInterruptedSessions: () => void;

  // Status helpers
  getNetworkStatusText: () => string;
  getProtectionStatusText: () => string;
  isFullyProtected: () => boolean;
}

export const useRecordingProtection = (
  options: UseRecordingProtectionOptions = {}
): UseRecordingProtectionReturn => {
  const [state, setState] = useState<RecordingProtectionState>({
    isOnline: navigator.onLine,
    networkState: navigator.onLine ? 'online' : 'offline',
    isAppActive: true,
    appState: 'active',
    wasInterrupted: false,
    interruptionReason: undefined,
    savedChunksCount: 0,
    lastChunkSavedAt: null,
    isChunkStorageActive: false,
    hasInterruptedSessions: false,
    interruptedSessionCount: 0,
  });

  const {
    interruptedSessions,
    clearInterruptedSession: clearSession,
    clearAllInterruptedSessions: clearAllSessions,
    activeSession,
  } = useRecordingSessionStore();

  // Initialize services
  const initialize = useCallback(async () => {
    try {
      // Initialize app lifecycle service
      await appLifecycleService.initialize();

      // Initialize network monitor service
      networkMonitorService.initialize();

      // Initialize chunk storage service
      await chunkStorageService.initialize();

    } catch (error) {
      console.error('[useRecordingProtection] Initialization error:', error);
    }
  }, []);

  // Subscribe to app lifecycle changes
  useEffect(() => {
    const unsubscribe = appLifecycleService.subscribe((isActive, lifecycleState) => {
      console.log('[useRecordingProtection] App lifecycle change:', { isActive, lifecycleState });

      setState(prev => ({
        ...prev,
        isAppActive: isActive,
        appState: lifecycleState,
      }));

      if (!isActive) {
        options.onAppBackground?.();
      } else {
        options.onAppForeground?.();
      }
    });

    return unsubscribe;
  }, [options.onAppBackground, options.onAppForeground]);

  // Subscribe to network changes
  useEffect(() => {
    const unsubscribe = networkMonitorService.subscribe((isOnline, networkState) => {
      console.log('[useRecordingProtection] Network change:', { isOnline, networkState });

      setState(prev => ({
        ...prev,
        isOnline,
        networkState,
      }));

      if (!isOnline) {
        options.onNetworkOffline?.();
      } else {
        options.onNetworkOnline?.();
      }
    });

    return unsubscribe;
  }, [options.onNetworkOffline, options.onNetworkOnline]);

  // Track interrupted sessions
  useEffect(() => {
    setState(prev => ({
      ...prev,
      hasInterruptedSessions: interruptedSessions.length > 0,
      interruptedSessionCount: interruptedSessions.length,
    }));
  }, [interruptedSessions]);

  // Track active session interruption
  useEffect(() => {
    if (activeSession?.status === 'interrupted') {
      setState(prev => ({
        ...prev,
        wasInterrupted: true,
        interruptionReason: activeSession.interruptionReason,
      }));
      options.onInterruption?.(activeSession.interruptionReason);
    } else {
      setState(prev => ({
        ...prev,
        wasInterrupted: false,
        interruptionReason: undefined,
      }));
    }
  }, [activeSession?.status, activeSession?.interruptionReason, options.onInterruption]);

  // Track chunk storage state
  useEffect(() => {
    const checkChunkStorage = () => {
      setState(prev => ({
        ...prev,
        isChunkStorageActive: chunkStorageService.hasActiveSession(),
      }));
    };

    // Check initially
    checkChunkStorage();

    // Check periodically while mounted
    const interval = setInterval(checkChunkStorage, 1000);

    return () => clearInterval(interval);
  }, []);

  // Helper methods
  const getInterruptedSessions = useCallback((): RecordingSession[] => {
    return interruptedSessions;
  }, [interruptedSessions]);

  const clearInterruptedSession = useCallback((sessionId: string) => {
    clearSession(sessionId);
  }, [clearSession]);

  const clearAllInterruptedSessions = useCallback(() => {
    clearAllSessions();
  }, [clearAllSessions]);

  const getNetworkStatusText = useCallback((): string => {
    if (state.isOnline) {
      return 'Connected';
    }
    const info = networkMonitorService.getNetworkInfo();
    if (info.offlineDuration && info.offlineDuration > 60000) {
      const minutes = Math.floor(info.offlineDuration / 60000);
      return `Offline (${minutes}m)`;
    }
    return 'Offline';
  }, [state.isOnline]);

  const getProtectionStatusText = useCallback((): string => {
    const parts: string[] = [];

    if (!state.isOnline) {
      parts.push('Offline mode');
    }

    if (state.isChunkStorageActive) {
      parts.push('Auto-saving');
    }

    if (state.savedChunksCount > 0) {
      parts.push(`${state.savedChunksCount} chunks saved`);
    }

    if (parts.length === 0) {
      return 'Protected';
    }

    return parts.join(' • ');
  }, [state.isOnline, state.isChunkStorageActive, state.savedChunksCount]);

  const isFullyProtected = useCallback((): boolean => {
    // Fully protected if online and chunk storage is active (when recording)
    return state.isOnline && (state.isChunkStorageActive || !activeSession);
  }, [state.isOnline, state.isChunkStorageActive, activeSession]);

  return {
    ...state,
    initialize,
    getInterruptedSessions,
    clearInterruptedSession,
    clearAllInterruptedSessions,
    getNetworkStatusText,
    getProtectionStatusText,
    isFullyProtected,
  };
};
